import { useState, useEffect, useCallback, useRef } from 'react'
import { wsService, WebSocketMessage } from '@/services/websocket'

/**
 * Authorization tier levels
 */
export type AuthorizationTier = 'BUTTON_PRESS' | 'ELEVATED' | 'CRITICAL'

/**
 * Tool display configuration
 */
export interface ToolDisplayInfo {
  displayName: string
  getDetails: (params: Record<string, unknown>) => string
}

/**
 * Pending authorization request from the agent
 */
export interface PendingAuthorization {
  requestId: string
  toolName: string
  displayName: string
  details: string
  tier: AuthorizationTier
  params: Record<string, unknown>
  timestamp: Date
}

/**
 * Tool display name mappings
 */
const TOOL_DISPLAY_MAP: Record<string, ToolDisplayInfo> = {
  airtime: {
    displayName: 'Send Airtime',
    getDetails: (params) =>
      `${params.amount || '?'} to ${params.phone || 'unknown'}`,
  },
  email_send: {
    displayName: 'Send Email',
    getDetails: (params) => `To: ${params.recipient || params.to || 'unknown'}`,
  },
  mpesa_stk_push: {
    displayName: 'M-Pesa Payment',
    getDetails: (params) => `${params.amount || '?'} KES`,
  },
  scheduler: {
    displayName: 'Schedule Event',
    getDetails: (params) =>
      `${params.event_name || params.name || params.title || 'Event'}`,
  },
  uber_booking: {
    displayName: 'Book Uber',
    getDetails: (params) =>
      `${params.pickup || params.from || '?'} to ${params.destination || params.to || '?'}`,
  },
}

/**
 * Default display info for unknown tools
 */
function getDefaultDisplayInfo(toolName: string): ToolDisplayInfo {
  return {
    displayName: toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    getDetails: () => 'Requires your authorization',
  }
}

/**
 * Auto-deny timeout in seconds
 */
const AUTO_DENY_TIMEOUT = 30

/**
 * Hook to manage authorization state for agent tool calls
 */
export function useAuthorization() {
  const [pendingAuth, setPendingAuth] = useState<PendingAuthorization | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number>(AUTO_DENY_TIMEOUT)
  const timerRef = useRef<number | null>(null)
  const countdownRef = useRef<number | null>(null)

  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
  }, [])

  /**
   * Send authorization decision via WebSocket
   */
  const sendDecision = useCallback(
    (requestId: string, approved: boolean, pin?: string) => {
      wsService.send({
        type: 'authorization_decision',
        data: {
          request_id: requestId,
          approved,
          pin: pin || undefined,
        },
      })
    },
    []
  )

  /**
   * Approve the pending authorization
   */
  const approveAuth = useCallback(
    (pin?: string) => {
      if (!pendingAuth) return

      clearTimers()
      sendDecision(pendingAuth.requestId, true, pin)
      setPendingAuth(null)
      setTimeRemaining(AUTO_DENY_TIMEOUT)
    },
    [pendingAuth, clearTimers, sendDecision]
  )

  /**
   * Deny the pending authorization
   */
  const denyAuth = useCallback(() => {
    if (!pendingAuth) return

    clearTimers()
    sendDecision(pendingAuth.requestId, false)
    setPendingAuth(null)
    setTimeRemaining(AUTO_DENY_TIMEOUT)
  }, [pendingAuth, clearTimers, sendDecision])

  /**
   * Handle incoming authorization request
   */
  const handleAuthorizationRequired = useCallback(
    (message: WebSocketMessage) => {
      const data = message.data as {
        request_id: string
        tool_name: string
        tier: number
        tier_name: string
        tool_args: Record<string, unknown>
        params?: Record<string, unknown>
        message?: string
      }

      // Get tool display info
      const toolInfo = TOOL_DISPLAY_MAP[data.tool_name] || getDefaultDisplayInfo(data.tool_name)
      // Backend sends tool_args, but we use params internally
      const params = data.tool_args || data.params || {}
      // Backend sends tier_name as the string version
      const tier = (data.tier_name || 'BUTTON_PRESS') as AuthorizationTier

      const authRequest: PendingAuthorization = {
        requestId: data.request_id,
        toolName: data.tool_name,
        displayName: toolInfo.displayName,
        details: toolInfo.getDetails(params),
        tier: tier,
        params: params,
        timestamp: new Date(),
      }

      // Clear any existing timers
      clearTimers()

      // Set the new pending auth
      setPendingAuth(authRequest)
      setTimeRemaining(AUTO_DENY_TIMEOUT)

      // Start countdown
      countdownRef.current = window.setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            return 0
          }
          return prev - 1
        })
      }, 1000)

      // Set auto-deny timer
      timerRef.current = window.setTimeout(() => {
        sendDecision(authRequest.requestId, false)
        setPendingAuth(null)
        setTimeRemaining(AUTO_DENY_TIMEOUT)
        clearTimers()
      }, AUTO_DENY_TIMEOUT * 1000)
    },
    [clearTimers, sendDecision]
  )

  /**
   * Subscribe to WebSocket events
   */
  useEffect(() => {
    const unsubscribe = wsService.on('authorization_required', handleAuthorizationRequired)

    return () => {
      unsubscribe()
      clearTimers()
    }
  }, [handleAuthorizationRequired, clearTimers])

  return {
    pendingAuth,
    timeRemaining,
    approveAuth,
    denyAuth,
  }
}
